// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/AgentSteps.test.tsx
import { type AskPunttAgentStep } from "@mg/schemas/src/christo/askPuntt";
import {
  CaretDown,
  CaretRight,
  CheckCircle,
  CircleNotch,
} from "@phosphor-icons/react";
import { Text } from "@radix-ui/themes";
import cx from "classnames";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  STATUS_PANEL_ITEMS_MUTED_CLASS,
  STATUS_PANEL_TOGGLE_HOVER_CLASS,
  STATUS_PANEL_TOGGLE_MUTED_CLASS,
} from "./statusPanelTokens";
// why: the salvage parser lives in a leaf module now (review #5399) -- it is
// pure string/JSON logic, this file was already past the 400-line ceiling, and
// the cut-point cases are only falsifiable when they can be called directly.
import {
  capabilityInstructions,
  parseTruncatedJson,
  stringField,
} from "./truncatedJson";

import { interactiveSurfaceClasses } from "../../../components/InteractiveSurface";

type StepPair = {
  call: AskPunttAgentStep & { type: "tool_call" };
  result?: AskPunttAgentStep & { type: "tool_result" };
};

type SubagentRequest = {
  name: string;
  task: string;
  /**
   * Slot in the RAW `args.requests` array, kept because warhol's
   * `run_subagents` returns "one report per request, in order" over that raw
   * list (`zip(requests, outcomes, strict=True)` in `agents/spawn.py`) -- even
   * for a request this panel drops, which still gets a `failed` report in its
   * slot from `_lookup`. Matching reports by position AFTER the drop shifted
   * every later report up one row, so a surviving subagent showed its
   * neighbour's verdict.
   */
  sourceIndex: number;
};

type SubagentReportView = {
  name?: string;
  status?: string;
  summary?: string;
  error?: string;
  output?: string;
};

type SubagentRequestEntry = {
  request: SubagentRequest;
  index: number;
  report?: SubagentReportView;
};

type SubagentRequestGroup = {
  name: string;
  entries: SubagentRequestEntry[];
};

function parseSubagentRequests(
  args: Record<string, unknown> | undefined,
): SubagentRequest[] {
  const requests = args?.requests;
  if (!Array.isArray(requests)) return [];
  return requests
    .map((entry, sourceIndex) => {
      if (entry == null || typeof entry !== "object") return null;
      const row = entry as Record<string, unknown>;
      const name = stringField(row.name);
      const task = stringField(row.task);
      if (!name) return null;
      return { name, task, sourceIndex };
    })
    .filter((row): row is SubagentRequest => row != null);
}

function parseSubagentReports(summary: string): SubagentReportView[] | null {
  const trimmed = summary.trim();
  if (!trimmed.startsWith("[")) return null;
  const parsed = parseTruncatedJson(trimmed);
  if (!Array.isArray(parsed)) return null;
  return parsed.map((entry) =>
    entry != null && typeof entry === "object"
      ? (entry as SubagentReportView)
      : {},
  );
}

function parseSubagentReport(summary: string): SubagentReportView | null {
  const trimmed = summary.trim();
  if (!trimmed.startsWith("{")) return null;
  const parsed = parseTruncatedJson(trimmed);
  return parsed != null && typeof parsed === "object"
    ? (parsed as SubagentReportView)
    : null;
}

function groupConsecutiveSubagentRequests(
  requests: SubagentRequest[],
  reports: SubagentReportView[] | null,
): SubagentRequestGroup[] {
  const groups: SubagentRequestGroup[] = [];
  for (let index = 0; index < requests.length; index += 1) {
    const request = requests[index];
    const last = groups[groups.length - 1];
    const entry: SubagentRequestEntry = {
      request,
      index,
      // By the request's slot in the wire array, not its row here: a dropped
      // request still owns a report slot upstream.
      report: reports?.[request.sourceIndex],
    };
    if (last && last.name === request.name) {
      last.entries.push(entry);
      continue;
    }
    groups.push({ name: request.name, entries: [entry] });
  }
  return groups;
}

function SubagentSameNameGroup({
  stepId,
  groupIndex,
  name,
  entries,
  done,
}: {
  stepId: string;
  groupIndex: number;
  name: string;
  entries: SubagentRequestEntry[];
  done: boolean;
}) {
  const { t } = useTranslation("ui");
  const [expanded, setExpanded] = useState(false);
  const rowId = `${stepId}-${groupIndex}`;
  const label = t("agentSteps.subagentGroupLabel", {
    name,
    count: entries.length,
  });

  return (
    <div
      data-testid={`agent-step-subagent-group-${rowId}`}
      className={cx(
        "overflow-hidden",
        interactiveSurfaceClasses({
          expandable: true,
          muted: true,
          compact: true,
          open: expanded,
        }),
      )}
    >
      <button
        type="button"
        data-testid={`agent-step-subagent-group-toggle-${rowId}`}
        aria-expanded={expanded}
        aria-label={label}
        onClick={() => setExpanded((open) => !open)}
        className={`${STATUS_PANEL_TOGGLE_MUTED_CLASS} ${STATUS_PANEL_TOGGLE_HOVER_CLASS}`}
      >
        {done ? (
          <CheckCircle
            size={14}
            weight="fill"
            className="shrink-0 text-malachite-700"
            aria-hidden
          />
        ) : (
          <CircleNotch
            size={14}
            className="shrink-0 animate-spin text-puntt-accent-9 motion-reduce:animate-none"
            aria-hidden
          />
        )}
        <Text
          size="1"
          weight="regular"
          data-testid="agent-step-name"
          className="min-w-0 flex-1 truncate text-puntt-neutral-gray-12"
        >
          {label}
        </Text>
        {expanded ? (
          <CaretDown
            size={14}
            className="shrink-0 text-puntt-neutral-gray-12"
            aria-hidden
          />
        ) : (
          <CaretRight
            size={14}
            className="shrink-0 text-puntt-neutral-gray-12"
            aria-hidden
          />
        )}
      </button>
      {expanded ? (
        <div
          data-testid={`agent-step-subagent-group-body-${rowId}`}
          className={`${STATUS_PANEL_ITEMS_MUTED_CLASS} space-y-0.5`}
        >
          {entries.map((entry) => (
            <SubagentToolStep
              key={`${stepId}-${entry.index}`}
              stepId={stepId}
              requestIndex={entry.index}
              title={entry.request.name}
              body={entry.request.task}
              report={entry.report}
              done={done}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function reportDetail(report: SubagentReportView | undefined): string {
  if (!report) return "";
  const summary = stringField(report.summary);
  if (summary) return summary;
  const error = stringField(report.error);
  if (error) return error;
  const output = stringField(report.output);
  if (output) return output;
  const status = stringField(report.status);
  return status;
}

function SubagentToolStep({
  stepId,
  requestIndex,
  title,
  body,
  report,
  done,
}: {
  stepId: string;
  requestIndex?: number;
  title: string;
  body: string;
  report?: SubagentReportView;
  done: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const suffix = requestIndex != null ? `-${requestIndex}` : "";
  const rowId = `${stepId}${suffix}`;
  const reportText = reportDetail(report);

  return (
    <div
      data-testid={`agent-step-subagent-${rowId}`}
      className={cx(
        "overflow-hidden",
        interactiveSurfaceClasses({
          expandable: true,
          muted: true,
          compact: true,
          open: expanded,
        }),
      )}
    >
      <button
        type="button"
        data-testid={`agent-step-subagent-toggle-${rowId}`}
        aria-expanded={expanded}
        aria-label={title}
        onClick={() => setExpanded((open) => !open)}
        className={`${STATUS_PANEL_TOGGLE_MUTED_CLASS} ${STATUS_PANEL_TOGGLE_HOVER_CLASS}`}
      >
        {done ? (
          <CheckCircle
            size={14}
            weight="fill"
            className="shrink-0 text-malachite-700"
            aria-hidden
          />
        ) : (
          <CircleNotch
            size={14}
            className="shrink-0 animate-spin text-puntt-accent-9 motion-reduce:animate-none"
            aria-hidden
          />
        )}
        <Text
          size="1"
          weight="regular"
          data-testid="agent-step-name"
          className="min-w-0 flex-1 truncate text-puntt-neutral-gray-12"
        >
          {title}
        </Text>
        {expanded ? (
          <CaretDown
            size={14}
            className="shrink-0 text-puntt-neutral-gray-12"
            aria-hidden
          />
        ) : (
          <CaretRight
            size={14}
            className="shrink-0 text-puntt-neutral-gray-12"
            aria-hidden
          />
        )}
      </button>
      {expanded ? (
        <div
          data-testid={`agent-step-subagent-body-${rowId}`}
          className={STATUS_PANEL_ITEMS_MUTED_CLASS}
        >
          {body ? (
            <Text
              size="1"
              className="whitespace-pre-wrap text-puntt-neutral-gray-12"
            >
              {body}
            </Text>
          ) : null}
          {reportText ? (
            <Text
              size="1"
              className={`whitespace-pre-wrap text-puntt-neutral-gray-9 ${body ? "mt-1" : ""}`}
            >
              {reportText}
            </Text>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** One expandable row per subagent spawn / skill load. */
export function SubagentToolStepsForPair({
  pair,
  isStreaming,
}: {
  pair: StepPair;
  isStreaming: boolean;
}) {
  const call = pair.call;
  const done = !!pair.result || !isStreaming;
  const stepId = call.stepId || call.timestamp;
  const args = call.args ?? {};

  if (call.tool === "run_subagents") {
    const requests = parseSubagentRequests(args);
    const reports = pair.result
      ? parseSubagentReports(pair.result.summary)
      : null;
    if (requests.length === 0) {
      // why: rembrandt converts a `tool_call` into a `tool_result` in the SAME
      // array slot, and the `tool_result` schema carries no `args` -- so a
      // settled fan-out, and EVERY reloaded thread, reaches this panel with no
      // `requests` at all and the reports JSON alone in `summary`. The bare
      // fallback row passed no report, so each finished fan-out collapsed to
      // one nameless row whose expanded box was empty: every subagent and its
      // verdict was dropped. That is the same wire fact `run_subagent` and
      // `load_capability` already recover from below.
      if (reports && reports.length > 0) {
        return (
          <>
            {reports.map((report, reportIndex) => (
              <SubagentToolStep
                key={`${stepId}-${reportIndex}`}
                stepId={stepId}
                requestIndex={reportIndex}
                title={
                  stringField(report.name) ||
                  call.displayName ||
                  "Run subagents"
                }
                body=""
                report={report}
                done={done}
              />
            ))}
          </>
        );
      }
      return (
        <SubagentToolStep
          stepId={stepId}
          title={call.displayName || "Run subagents"}
          body=""
          done={done}
        />
      );
    }
    const groups = groupConsecutiveSubagentRequests(requests, reports);
    return (
      <>
        {groups.map((group, groupIndex) =>
          group.entries.length === 1 ? (
            <SubagentToolStep
              key={`${stepId}-${group.entries[0].index}`}
              stepId={stepId}
              requestIndex={group.entries[0].index}
              title={group.entries[0].request.name}
              body={group.entries[0].request.task}
              report={group.entries[0].report}
              done={done}
            />
          ) : (
            <SubagentSameNameGroup
              key={`${stepId}-group-${groupIndex}`}
              stepId={stepId}
              groupIndex={groupIndex}
              name={group.name}
              entries={group.entries}
              done={done}
            />
          ),
        )}
      </>
    );
  }

  if (call.tool === "run_subagent") {
    const name = stringField(args.name) || call.displayName || "Run subagent";
    const task = stringField(args.task);
    const report = pair.result
      ? (parseSubagentReport(pair.result.summary) ?? undefined)
      : undefined;
    return (
      <SubagentToolStep
        stepId={stepId}
        title={name}
        body={task}
        report={report}
        done={done}
      />
    );
  }

  if (call.tool === "load_capability") {
    const instructions =
      stringField(args.instructions) ||
      capabilityInstructions(pair.result?.summary);
    return (
      <SubagentToolStep
        stepId={stepId}
        title={call.displayName || "Load skill"}
        body={instructions}
        done={done}
      />
    );
  }

  return null;
}

export function countSubagentLogicalSteps(
  tool: string,
  args: Record<string, unknown> | undefined,
  /**
   * The merged `tool_result` summary, when one landed. It is the only place a
   * settled fan-out still lists its subagents — the in-place merge drops
   * `args.requests` — so without it the header counted a finished 3-subagent
   * fan-out as "1 Step" and disagreed with the rows rendered under it.
   */
  resultSummary?: string,
): number {
  if (tool === "run_subagents") {
    const requests = parseSubagentRequests(args);
    if (requests.length > 0) return requests.length;
    const reports = resultSummary ? parseSubagentReports(resultSummary) : null;
    return reports && reports.length > 0 ? reports.length : 1;
  }
  return 1;
}
